/**
 * In-process `globalThis.fetch` wrapper for auth-token-bearing
 * traffic. Mirrors the existing `llmProxyShim.js` pattern: the page is
 * the only execution context, so there's no SW lifecycle race, no
 * controller-null window on Cmd+Shift+R, no CDN-fronted activation
 * gap. (We tried the SW route for the LLM proxy and tore it out —
 * `llmProxyShim.js:1-13`.)
 *
 * Responsibilities (both gated on `isAllowedOrigin` — the URL must
 * resolve to the dashboard's own origin or the build-time API
 * gateway origin; see O-1786):
 *
 *   1. **Capture**: when `/v1/auth/{exchange,refresh,select-organization,
 *      token}` returns 2xx with tokens in the body, peel them out into
 *      the in-process store (`authTokenStore.js`) and rewrite the
 *      response so the page-side caller sees the body **without** the
 *      raw tokens. Page JS, the DevTools "Response" tab, and anything
 *      that does `await res.json()` all see a sanitized payload.
 *
 *   2. **Attach**: every other `/v1/*` request gets `Authorization:
 *      Bearer <access>` from the store, replacing the per-call
 *      injection that used to live in `restClient.js`.
 *
 * Without the origin gate, a `fetch()` issued from anywhere in the
 * dashboard to a third-party URL whose pathname happens to start
 * with `/v1/` would (1) leak the WorkOS bearer to that host, and (2)
 * if the path also ended with `/v1/auth/*` and the response body was
 * 2xx JSON, plant attacker-chosen tokens into the in-process store
 * and broadcast them to every sibling tab. The MCP OAuth flow in
 * `dashboard/src/lib/mcp/oauth.js` is the concrete attacker-reachable
 * fetch — it POSTs to a `token_endpoint` URL pulled verbatim out of
 * the MCP server's discovery document.
 *
 * Install order in `main.jsx`: this shim goes **before**
 * `installLlmProxyShim()` so the LLM shim's `origFetch` capture wraps
 * this shim. That makes the call chain `llmWrapper → authWrapper →
 * native`: LLM-host rewrites still land on `/v1/tenant/llm-proxy`,
 * which then picks up the Authorization header from our store on the
 * way out.
 *
 * What this is NOT: a refresh-on-401 layer. Refresh dedup +
 * peer-tab coordination + terminal-error logout still lives in
 * `authBridge.attemptRefresh()`; we just capture the refreshed tokens
 * here when the refresh call comes back.
 */

import { setTokens } from "./authTokenStore.js";
import { broadcastTokens } from "./authBroadcast.js";
import { API_BASE_URL } from "./featureFlags.js";

// URL patterns whose response bodies carry tokens we need to capture.
// Anchored to the path so a query string or absolute prefix doesn't
// throw off the match. Only consulted after an origin allowlist check
// (see `isAllowedOrigin`) so a third-party host can't ride the
// suffix match to plant tokens.
const AUTH_RESPONSE_PATHS = [
    "/v1/auth/exchange",
    "/v1/auth/refresh",
    "/v1/auth/select-organization",
    "/v1/auth/token",
];

// Pre-compute the API gateway origin from the build's
// `VITE_API_BASE_URL`. Empty when the dev-proxy branch is active
// (relative `/v1/...` paths handled via `location.origin`).
const API_BASE_ORIGIN = (() => {
    try {
        if (API_BASE_URL && /^https?:\/\//.test(API_BASE_URL)) {
            return new URL(API_BASE_URL).origin;
        }
    } catch {}
    return null;
})();

let installed = false;
let origFetch = null;

/**
 * Idempotent install. Call once at app boot, before any code that
 * issues an authed fetch (i.e. before `bootstrapAuth()` and before
 * `installLlmProxyShim()`). Returns nothing — the wrapper is in place
 * synchronously.
 */
export function installAuthFetchShim() {
    if (installed) return;
    if (typeof globalThis === "undefined" || typeof globalThis.fetch !== "function") return;

    // One-time migration: tokens that used to live in localStorage are
    // now in-process. Clear the legacy keys so a returning user with
    // an old cached login doesn't carry plaintext tokens forward in
    // storage. The shim will capture fresh tokens on the next exchange
    // / refresh round-trip; in the meantime the user is treated as
    // logged out and bootstrap redirects through WorkOS as needed.
    clearLegacyLocalStorageTokens();

    installed = true;
    origFetch = globalThis.fetch.bind(globalThis);
    globalThis.fetch = wrappedFetch;
}

function clearLegacyLocalStorageTokens() {
    try {
        if (typeof localStorage === "undefined") return;
        localStorage.removeItem("auth_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("workos_user");
        // `tenant_id` was already documented stale in clearUserData.js;
        // sweep it for the same reason.
        localStorage.removeItem("tenant_id");
    } catch {}
}

function extractUrl(input) {
    if (typeof input === "string") return input;
    if (typeof URL !== "undefined" && input instanceof URL) return input.href;
    if (input && typeof input.url === "string") return input.url;
    return null;
}

// Parse a fetch URL into `{ path, origin }` using the WHATWG URL
// parser — the same algorithm `fetch()` uses to resolve its input.
// Critical: do NOT do any static-string classification ("looks
// relative", "starts with `//`", etc.) ahead of the parse. The
// parser normalizes leading C0/whitespace, normalizes `\\` to `//`
// for special schemes, and resolves relative-or-absolute against the
// base — a static check on the input string disagrees with all
// three transforms and would let inputs like `" https://attacker/v1"`
// or `"\\\\attacker/v1"` fool the allowlist into seeing a "relative"
// URL while `fetch()` actually dispatches to attacker. See O-1786.
function parseUrl(url) {
    if (typeof url !== "string" || url.length === 0) return null;
    try {
        // Base is chosen so that a *truly* relative URL resolves to
        // an allowlisted origin. Prefer `location.origin` (always
        // present in a browser); fall back to `API_BASE_ORIGIN` when
        // running outside a browser context (tests / SSR). For any
        // input the URL parser resolves to a cross-origin host, the
        // parsed `origin` is the resolved origin — not the base — so
        // `isAllowedOrigin` rejects it on the equality check, exactly
        // as we want.
        const base = typeof location !== "undefined"
            ? location.origin
            : (API_BASE_ORIGIN || "http://localhost");
        const parsed = new URL(url, base);
        return { path: parsed.pathname, origin: parsed.origin };
    } catch {
        return null;
    }
}

// Origin allowlist gate. Derived entirely from the *parsed* URL's
// origin so any input the URL parser resolves to a cross-origin host
// is correctly rejected — including inputs where a naïve static-
// string check would misclassify the input as relative (leading
// whitespace, backslash-prefix, etc.). See O-1786.
//
// Allowed origins:
//   - `location.origin` — same-origin requests (the page itself, plus
//     any URL that the WHATWG parser resolves against our base to our
//     own origin, including legitimately-relative paths).
//   - `API_BASE_ORIGIN` — the build-time API gateway. Covers prod's
//     cross-origin gateway URL and the test harness's mocked base.
//
// Anything else — a third-party `https://attacker.example/v1/...`
// fetched via `globalThis.fetch` from anywhere in the dashboard
// (notably `dashboard/src/lib/mcp/oauth.js` which POSTs to MCP-server-
// supplied `token_endpoint` URLs) — is rejected: the shim must not
// leak the WorkOS bearer to it, and must not capture an
// `access_token`/`refresh_token` field out of its response body.
function isAllowedOrigin(parsed) {
    if (!parsed) return false;
    if (typeof location !== "undefined" && parsed.origin === location.origin) return true;
    if (API_BASE_ORIGIN && parsed.origin === API_BASE_ORIGIN) return true;
    return false;
}

function isAuthResponsePath(path) {
    if (!path) return false;
    return AUTH_RESPONSE_PATHS.some((p) => path === p || path.endsWith(p));
}

function isV1Path(path) {
    return typeof path === "string" && path.startsWith("/v1/");
}

async function wrappedFetch(input, init = {}) {
    const url = extractUrl(input);
    const parsed = parseUrl(url);
    const path = parsed?.path ?? null;
    const allowed = isAllowedOrigin(parsed);

    if (allowed && isV1Path(path) && !isAuthResponsePath(path)) {
        // Steady-state API call: attach Authorization from the store.
        // Imported lazily to avoid a circular import (authBridge ↔
        // authTokenStore) and to pick up the latest token on every
        // call without caching at module scope.
        const { getAccessToken } = await import("./authTokenStore.js");
        const token = getAccessToken();
        if (token) {
            const headers = mergeAuthHeader(init.headers, token);
            init = { ...init, headers };
        }
    }

    const res = await origFetch(input, init);

    if (allowed && isAuthResponsePath(path) && res.ok) {
        return await captureAndSanitize(res);
    }

    return res;
}

// Build a Headers-like object that carries the caller's headers plus
// Authorization. Accepts the three shapes fetch tolerates: Headers
// instance, plain object, or 2-tuple array.
function mergeAuthHeader(headers, token) {
    const merged = new Headers(headers || {});
    merged.set("Authorization", `Bearer ${token}`);
    return merged;
}

// Read the response body, capture access/refresh tokens (+ user) into
// the store, and return a new Response whose body has those fields
// stripped. The original `res` is consumed; callers receive the
// sanitized clone.
async function captureAndSanitize(res) {
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) return res;

    let text;
    try {
        text = await res.text();
    } catch (err) {
        // A torn body stream on an otherwise-2xx auth response looks
        // identical to "server returned empty body" if we just swallow
        // it — log so a debugging session can tell the difference. We
        // still fall through to the empty-body path: the caller
        // (authBootstrap) treats a token-less exchange as a failed
        // exchange, which trips the O-1321 circuit breaker — the
        // correct outcome here.
        console.warn("[authFetchShim] failed to read auth response body:", err);
        text = "";
    }
    if (!text) return new Response(text, { status: res.status, headers: res.headers });

    let parsed;
    try {
        parsed = JSON.parse(text);
    } catch {
        // Non-JSON body despite the content-type — leave it alone.
        return new Response(text, { status: res.status, headers: res.headers });
    }

    const rawAccess = parsed?.access_token ?? parsed?.accessToken ?? null;
    const rawRefresh =
        parsed?.refresh_token ?? parsed?.refreshToken ?? parsed?.refresh ?? null;
    const rawUser = parsed?.user ?? null;

    // Defense in depth on top of the origin allowlist (see
    // `isAllowedOrigin`): even from an allowed origin, only accept
    // string tokens. Limits blast radius of a future allowlist gap or
    // an unexpected gateway response shape — without this guard,
    // anything (object, array, number) would be written into the
    // store and propagated cross-tab via BroadcastChannel.
    const access = typeof rawAccess === "string" && rawAccess.length > 0 ? rawAccess : null;
    const refresh = typeof rawRefresh === "string" && rawRefresh.length > 0 ? rawRefresh : null;
    const user = rawUser && typeof rawUser === "object" && !Array.isArray(rawUser) ? rawUser : null;

    if (access || refresh) {
        setTokens({
            access: access ?? undefined,
            refresh: refresh ?? undefined,
            user: user ?? undefined,
        });
        // Push the same payload to sibling tabs so they share the
        // session without each running their own WorkOS exchange.
        // No-op if BroadcastChannel isn't started yet (tests, or
        // builds where the channel is unavailable).
        broadcastTokens({ access, refresh, user });
    }

    // Strip token fields from the body the page sees. Leaves `user`
    // and any other fields alone.
    const sanitized = { ...parsed };
    delete sanitized.access_token;
    delete sanitized.accessToken;
    delete sanitized.refresh_token;
    delete sanitized.refreshToken;
    delete sanitized.refresh;

    // Rebuild headers minus content-length (we changed the body
    // length) so downstream code that trusts content-length doesn't
    // get a mismatched value.
    const headers = new Headers(res.headers);
    headers.delete("content-length");

    return new Response(JSON.stringify(sanitized), {
        status: res.status,
        statusText: res.statusText,
        headers,
    });
}

// Test-only: reset shim state between unit tests. Not exported from
// the package's public surface — imported by name in the test file.
export function _resetForTests() {
    if (installed && origFetch) {
        globalThis.fetch = origFetch;
    }
    installed = false;
    origFetch = null;
}
